import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import api from 'src/services/BaseService';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';
import Iconify from 'src/components/iconify';
import { ACTION_TYPES, appendMallAlertBlockRecord } from './mallAlertBlockData';

const TAB_WARNING = 'warning';
const TAB_BLOCK = 'block';

const ROWS_PER_PAGE_OPTIONS = [20, 30, 50, 100];
const DEFAULT_LIMIT = 20;
const FILTER_MIN_LENGTH = 3;

const COMPACT_PAGINATION_SX = {
  '& .MuiTablePagination-toolbar': {
    minHeight: 'auto',
  },
  '& .MuiTablePagination-selectLabel': {
    marginBottom: 0,
  },
  '& .MuiTablePagination-input': {
    marginRight: '8px',
    marginLeft: '4px',
  },
  '& .MuiTablePagination-displayedRows': {
    marginLeft: 0,
    marginRight: '8px',
  },
  '& .MuiTablePagination-actions': {
    marginLeft: '4px',
  },
};

const getMallTitle = (mallID) => {
  const user = tokenService.getUser();
  const mall = user?.authLocations?.mall?.find(
    (item) => String(item.id) === String(mallID)
  );
  return mall?.title || '';
};

const normalizeStore = (store) => ({
  id: store.id,
  branchID: store.branchID || store.id,
  mallID: store.mallID,
  cariKod: store.companyCorporationalCode || '-',
  ticariUnvan: store.companyLegalTitle || store.title || '-',
  storeTitle: store.title || '-',
  mallTitle: getMallTitle(store.mallID),
  vergiNo: store.billing?.taxNo || '-',
  warnings: Array.isArray(store.warnings) ? store.warnings : [],
  isBlocked: String(store.isBlocked) === '1',
});

const statusChipSx = {
  borderRadius: '999px',
  fontWeight: 600,
  minWidth: 88,
};

const getRowActiveStatus = (store, tab) =>
  tab === TAB_WARNING ? store.warnings.length > 0 : store.isBlocked;

const STATUS_ALL = 'all';
const STATUS_ACTIVE = 'active';
const STATUS_INACTIVE = 'inactive';

const STATUS_FILTER_OPTIONS = {
  [TAB_WARNING]: [
    { value: STATUS_ALL, label: 'Tümü' },
    { value: STATUS_ACTIVE, label: 'Uyarıda Olanlar' },
    { value: STATUS_INACTIVE, label: 'Uyarıda Olmayanlar' },
  ],
  [TAB_BLOCK]: [
    { value: STATUS_ALL, label: 'Tümü' },
    { value: STATUS_ACTIVE, label: 'Blokajda Olanlar' },
    { value: STATUS_INACTIVE, label: 'Blokajda Olmayanlar' },
  ],
};

const matchesStatusFilter = (store, tab, statusFilter) => {
  if (statusFilter === STATUS_ALL) return true;
  const isActive = getRowActiveStatus(store, tab);
  return statusFilter === STATUS_ACTIVE ? isActive : !isActive;
};

// Sütun başlıklarının metni önemli değil: 1. sütun = Muhasebe Kodu,
// 2. sütun = Cari Kod, 3. sütun (opsiyonel) = Uyarı Mesajı. Eşleştirme
// başlık ismine göre değil sütun sırasına göre yapılır.
const mapExcelRowByPosition = (rowArray) => ({
  accountCode: String(rowArray?.[0] ?? '').trim(),
  cariKod: String(rowArray?.[1] ?? '').trim(),
  message: String(rowArray?.[2] ?? '').trim(),
});

export default function MallAlertBlockSystem() {
  const [activeTab, setActiveTab] = useState(TAB_WARNING);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    cariKod: '',
    ticariUnvan: '',
    vergiNo: '',
  });
  // AVM filtresi: dropdown'da gösterilecek {id, title} listesi ve kullanıcının
  // çoklu seçtiği AVM'ler.
  const [mallOptions, setMallOptions] = useState([]);
  const [selectedMalls, setSelectedMalls] = useState([]);
  const [mallSearch, setMallSearch] = useState('');
  const mallSearchRef = useRef(null);
  // Aktif sekmeye göre durum filtresi (uyarıda olan/olmayan, blokajda
  // olan/olmayan). Backend'e gönderilmiyor, yüklenen liste üzerinde uygulanıyor.
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);

  // MUI, menü her yeniden render olduğunda odağı ilk MenuItem'a kaydırıyor;
  // arama kutusundaki her karakter değişiminde odağı geri veriyoruz.
  useEffect(() => {
    if (mallSearchRef.current) {
      mallSearchRef.current.focus();
    }
  }, [mallSearch]);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalRecord, setTotalRecord] = useState(0);
  const [totalLoading, setTotalLoading] = useState(false);
  const totalRecordAbortRef = useRef(null);
  const [appliedFilter, setAppliedFilter] = useState(null);
  // AVM seçimi backend'e `filter` objesinin içinde değil, ayrı bir
  // `mallID` parametresi olarak gönderiliyor.
  const [appliedMallIDs, setAppliedMallIDs] = useState([]);
  // Backend, istenen limit'ten daha az kayıt döndürebiliyor (son sayfa
  // olmasa bile). Bu yüzden sayfa başına gerçek gösterim aralığını
  // start/limit'ten değil, her sayfada fiilen dönen kayıt sayısından
  // biriktirerek hesaplıyoruz: pageOffsets[N] = N. sayfadan önce
  // gösterilmiş toplam satır sayısı.
  const [pageOffsets, setPageOffsets] = useState({ 0: 0 });
  // AVM'lerin muhasebe kodu, mağaza kaydında değil AVM kaydında bulunuyor;
  // tabloda göstermek için mallID -> muhasebe kodu eşlemesini ayrıca çekiyoruz.
  const [mallAccountCodes, setMallAccountCodes] = useState({});
  const isFiltered = Boolean(appliedFilter);
  // Filtre aktifken backend tüm eşleşen kayıtları tek seferde döndürüyor
  // (start/limit yok); sayfalama bu durumda client-side, zaten elimizde
  // olan `stores` dizisi üzerinde yapılıyor — hızlı ve API çağrısız.
  const page = limit > 0 ? Math.floor(start / limit) : 0;
  const displayStart = isFiltered ? start : pageOffsets[page] ?? page * limit;
  const statusFilteredStores = stores.filter((store) =>
    matchesStatusFilter(store, activeTab, statusFilter)
  );
  const lastPage =
    limit > 0
      ? Math.max(
          0,
          Math.ceil(
            (isFiltered ? statusFilteredStores.length : totalRecord) / limit
          ) - 1
        )
      : 0;
  const filtersAreEmpty =
    !filters.cariKod.trim() &&
    !filters.ticariUnvan.trim() &&
    !filters.vergiNo.trim() &&
    selectedMalls.length === 0;

  const getAccountCode = (store) => mallAccountCodes[store.mallID] || '-';

  // Excel içe aktarım
  const [excelProcessing, setExcelProcessing] = useState(false);
  const [excelStep, setExcelStep] = useState({
    label: '',
    current: 0,
    total: 0,
  });
  const [excelResult, setExcelResult] = useState(null);
  const [excelReviewOpen, setExcelReviewOpen] = useState(false);
  const [excelSubmitting, setExcelSubmitting] = useState(false);

  const loadStores = (
    nextStart = start,
    nextLimit = limit,
    filter = appliedFilter,
    nextID = null,
    mallIDs = appliedMallIDs
  ) => {
    setLoading(true);

    const payload = filter
      ? {
          filter,
          ...(mallIDs && mallIDs.length > 0 ? { mallID: mallIDs } : {}),
        }
      : { start: nextStart, limit: nextLimit, ...(nextID ? { nextID } : {}) };
    const loadedPage =
      !filter && nextLimit > 0 ? Math.floor(nextStart / nextLimit) : 0;

    return api
      .post('flow/get-malls-branches', payload)
      .then((response) => {
        const branches = Array.isArray(response?.data?.result)
          ? response.data.result
          : [];

        setStores(branches.map(normalizeStore));

        if (!filter) {
          setPageOffsets((prev) => {
            const currentOffset = prev[loadedPage] ?? loadedPage * nextLimit;
            return {
              ...prev,
              [loadedPage]: currentOffset,
              [loadedPage + 1]: currentOffset + branches.length,
            };
          });
        }
      })
      .catch(() => {
        toast.error('Mağaza listesi alınamadı.');
        setStores([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadTotalRecord = (
    filter = appliedFilter,
    mallIDs = appliedMallIDs
  ) => {
    // Filtre değişince (filtrele/temizle) önceki toplam kayıt isteği artık
    // geçersiz; devam ediyorsa iptal edip yerine yenisini başlatıyoruz.
    if (totalRecordAbortRef.current) {
      totalRecordAbortRef.current.abort();
    }
    const controller = new AbortController();
    totalRecordAbortRef.current = controller;

    const payload = filter
      ? {
          filter,
          ...(mallIDs && mallIDs.length > 0 ? { mallID: mallIDs } : {}),
        }
      : undefined;

    setTotalLoading(true);

    return api
      .post('flow/get-malls-branches-total-result-count', payload, {
        signal: controller.signal,
        skipLoader: true,
      })
      .then((response) => {
        if (totalRecordAbortRef.current !== controller) return;
        setTotalRecord(Number(response?.data?.result?.totalRecord) || 0);
      })
      .catch((error) => {
        if (totalRecordAbortRef.current !== controller) return;
        if (error?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
        setTotalRecord(0);
      })
      .finally(() => {
        if (totalRecordAbortRef.current !== controller) return;
        setTotalLoading(false);
        totalRecordAbortRef.current = null;
      });
  };

  useEffect(() => {
    loadStores(0, DEFAULT_LIMIT, null);
    loadTotalRecord(null);

    api
      .post('/malls/get-malls', { withOffices: true }, { skipLoader: true })
      .then((response) => {
        const malls = Array.isArray(response?.data?.result)
          ? response.data.result
          : [];
        const codesByMallID = {};
        const options = [];
        malls.forEach((mall) => {
          const mallID = mall.id ?? mall.mallID;
          if (mallID === undefined || mallID === null) return;
          codesByMallID[mallID] = String(
            mall?.accountCode || mall?.account_code || ''
          ).trim();
          options.push({ id: String(mallID), title: mall.title || '-' });
        });
        options.sort((a, b) =>
          a.title.localeCompare(b.title, 'tr', { sensitivity: 'base' })
        );
        setMallAccountCodes(codesByMallID);
        setMallOptions(options);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setStatusFilter(STATUS_ALL);
  }, [activeTab]);

  // Filtresizken `stores` zaten sunucudan gelen tek sayfalık veri; filtre
  // aktifken `stores` eşleşen tüm kayıtları içeriyor ve burada client-side
  // dilimleniyor. Durum filtresi her iki durumda da önce uygulanıyor.
  const filteredStores = isFiltered
    ? statusFilteredStores.slice(start, start + limit)
    : statusFilteredStores;

  const handleChangeStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setSelectedIds([]);
    if (isFiltered) setStart(0);
  };

  const handleChangePage = (_, newPage) => {
    const newStart = Math.max(0, newPage * limit);
    setStart(newStart);
    if (isFiltered) return;

    const isNext = newPage > page;
    const lastBranchID = isNext ? stores[stores.length - 1]?.branchID : null;
    loadStores(newStart, limit, null, lastBranchID);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setStart(0);
    if (isFiltered) return;
    setPageOffsets({ 0: 0 });
    loadStores(0, newLimit, null);
  };

  const handleApplyFilter = () => {
    const cariKod = filters.cariKod.trim();
    const ticariUnvan = filters.ticariUnvan.trim();
    const vergiNo = filters.vergiNo.trim();
    const mallIDs = selectedMalls.map((mall) => String(mall.id));

    if (cariKod && cariKod.length < FILTER_MIN_LENGTH) {
      toast.warning('Cari Kod alanına en az 3 karakter girmelisiniz.');
      return;
    }
    if (ticariUnvan && ticariUnvan.length < FILTER_MIN_LENGTH) {
      toast.warning('Cari Ünvan alanına en az 3 karakter girmelisiniz.');
      return;
    }
    if (vergiNo && vergiNo.length < FILTER_MIN_LENGTH) {
      toast.warning('Vergi Numarası alanına en az 3 karakter girmelisiniz.');
      return;
    }

    const filter = {
      companyCorporationalCode: cariKod,
      taxNo: vergiNo,
      companyLegalTitle: ticariUnvan,
    };

    setAppliedFilter(filter);
    setAppliedMallIDs(mallIDs);
    setStart(0);
    setPageOffsets({ 0: 0 });
    loadStores(0, limit, filter, null, mallIDs);
    loadTotalRecord(filter, mallIDs);
  };

  const displayedRowsLabel = () => {
    if (!isFiltered && totalLoading) {
      return `${displayStart + 1}–${
        displayStart + filteredStores.length
      } / Toplam kayıt sayısı hesaplanıyor...`;
    }
    const total = isFiltered ? statusFilteredStores.length : totalRecord;
    if (filteredStores.length === 0) return '0–0 / 0';
    return `${displayStart + 1}–${
      displayStart + filteredStores.length
    } / ${total}`;
  };

  const selectedStores = filteredStores.filter((store) =>
    selectedIds.includes(store.branchID)
  );

  const selectedStatus =
    selectedStores.length > 0
      ? getRowActiveStatus(selectedStores[0], activeTab)
      : null;
  const isRemoveFlow = selectedStatus === true;

  const eligibleStores = filteredStores.filter(
    (store) =>
      getRowActiveStatus(store, activeTab) === (selectedStatus ?? false)
  );

  const allSelected =
    eligibleStores.length > 0 &&
    eligibleStores.every((store) => selectedIds.includes(store.branchID));

  const verbLabel = isRemoveFlow
    ? activeTab === TAB_WARNING
      ? 'Uyarıyı Kaldır'
      : 'Blokajı Kaldır'
    : activeTab === TAB_WARNING
    ? 'Uyarı Listesine Ekle'
    : 'Blokaja Al';

  const actionIcon =
    activeTab === TAB_WARNING
      ? isRemoveFlow
        ? 'solar:bell-off-outline'
        : 'solar:bell-bing-outline'
      : isRemoveFlow
      ? 'solar:lock-unlocked-outline'
      : 'solar:lock-outline';

  const actionPalette = isRemoveFlow
    ? { main: '#166534', hover: '#14532D', soft: '#DCFCE7', softFg: '#166534' }
    : activeTab === TAB_WARNING
    ? { main: '#D97706', hover: '#B45309', soft: '#FEF3C7', softFg: '#D97706' }
    : { main: '#991B1B', hover: '#7F1D1D', soft: '#FEE2E2', softFg: '#EF4444' };

  const excelImportTab = excelResult?.actionTab || TAB_WARNING;
  const excelIsBlockImport = excelImportTab === TAB_BLOCK;
  const excelPalette = excelIsBlockImport
    ? { main: '#991B1B', hover: '#7F1D1D', soft: '#FEE2E2', softFg: '#B91C1C' }
    : { main: '#D97706', hover: '#B45309', soft: '#FEF3C7', softFg: '#B45309' };
  const excelActionIcon = excelIsBlockImport
    ? 'solar:lock-outline'
    : 'solar:bell-bing-outline';
  const excelGroupLabel = excelIsBlockImport
    ? 'Blokaja Alınacak Mağazalar'
    : 'Uyarıya Alınacak Mağazalar';

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(eligibleStores.map((store) => store.branchID));
  };

  const handleToggleOne = (branchID) => {
    setSelectedIds((prev) =>
      prev.includes(branchID)
        ? prev.filter((item) => item !== branchID)
        : [...prev, branchID]
    );
  };

  const handleSubmitAction = async () => {
    if (selectedStores.length === 0 || submitting) return;

    const user = tokenService.getUser();
    const token = user?.token;
    const userName = user?.name || user?.email || 'Yönetici';
    const branchIDs = selectedStores.map((store) => store.branchID);

    setSubmitting(true);

    const assertSuccess = (response, fallbackMessage) => {
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || fallbackMessage);
      }
      return response;
    };

    try {
      if (activeTab === TAB_WARNING) {
        if (isRemoveFlow) {
          const responses = await Promise.all(
            selectedStores.flatMap((store) =>
              store.warnings.map((warning) =>
                Services.RemoveBranchWarning(token, warning.warningID)
              )
            )
          );
          responses.forEach((response) =>
            assertSuccess(response, 'Uyarı kaldırılamadı.')
          );

          selectedStores.forEach((store) => {
            appendMallAlertBlockRecord({
              mallID: store.mallID,
              mallTitle: store.mallTitle,
              branchID: store.branchID,
              storeTitle: store.storeTitle,
              taxNo: store.vergiNo,
              userName,
              type: ACTION_TYPES.WARNING_REMOVED,
              message: '',
            });
          });

          toast.success('Uyarı kaldırıldı.');
        } else {
          const warningContent = message.trim();
          const response = await Services.AddBranchWarning(
            token,
            branchIDs,
            warningContent
          );
          assertSuccess(response, 'Uyarı listesine eklenemedi.');

          selectedStores.forEach((store) => {
            appendMallAlertBlockRecord({
              mallID: store.mallID,
              mallTitle: store.mallTitle,
              branchID: store.branchID,
              storeTitle: store.storeTitle,
              taxNo: store.vergiNo,
              userName,
              type: ACTION_TYPES.WARNING_ADDED,
              message: warningContent,
            });
          });

          toast.success('Uyarı listesine eklendi.');
        }
      } else {
        const response = isRemoveFlow
          ? await Services.RemoveBlockBranch(token, branchIDs)
          : await Services.BlockBranch(token, branchIDs);
        assertSuccess(
          response,
          isRemoveFlow ? 'Blokaj kaldırılamadı.' : 'Blokaja alınamadı.'
        );

        const storesWithWarnings = selectedStores.filter(
          (store) => store.warnings.length > 0
        );

        if (storesWithWarnings.length > 0) {
          const warningResponses = await Promise.all(
            storesWithWarnings.flatMap((store) =>
              store.warnings.map((warning) =>
                Services.RemoveBranchWarning(token, warning.warningID)
              )
            )
          );
          warningResponses.forEach((warningResponse) =>
            assertSuccess(warningResponse, 'Uyarı kaldırılamadı.')
          );

          storesWithWarnings.forEach((store) => {
            appendMallAlertBlockRecord({
              mallID: store.mallID,
              mallTitle: store.mallTitle,
              branchID: store.branchID,
              storeTitle: store.storeTitle,
              taxNo: store.vergiNo,
              userName,
              type: ACTION_TYPES.WARNING_REMOVED,
              message: '',
            });
          });
        }

        selectedStores.forEach((store) => {
          appendMallAlertBlockRecord({
            mallID: store.mallID,
            mallTitle: store.mallTitle,
            branchID: store.branchID,
            storeTitle: store.storeTitle,
            taxNo: store.vergiNo,
            userName,
            type: isRemoveFlow ? ACTION_TYPES.UNBLOCKED : ACTION_TYPES.BLOCKED,
            message: '',
          });
        });

        toast.success(isRemoveFlow ? 'Blokaj kaldırıldı.' : 'Blokaja alındı.');
      }

      setModalOpen(false);
      setMessage('');
      setSelectedIds([]);
      await loadStores();
    } catch (error) {
      toast.error(error?.message || 'İşlem gerçekleştirilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = () => {
    if (filteredStores.length === 0) return;

    const rows = filteredStores.map((store) => ({
      accountCode: getAccountCode(store),
      cariKod: store.cariKod,
      ticariUnvan: store.ticariUnvan,
      mallTitle: store.mallTitle || '-',
      storeTitle: store.storeTitle,
      vergiNo: store.vergiNo,
      durum:
        activeTab === TAB_WARNING
          ? store.warnings.length > 0
            ? 'Uyarıda'
            : '--'
          : store.isBlocked
          ? 'Blokajlı'
          : 'Açık',
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: rows,
        header: {
          'Muhasebe Kodu': 'string',
          'Cari Kod': 'string',
          'Ticari Ünvan': 'string',
          'AVM Adı': 'string',
          'Mağaza Adı': 'string',
          'Vergi No': 'string',
          Durum: 'string',
        },
        sheetTitle:
          activeTab === TAB_WARNING ? 'Uyarı Listesi' : 'Blokaj Listesi',
      })
      .then((response) => {
        if (response.data.status) {
          const newWindow = window.open(response.data.result, '_blank');
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed === 'undefined'
          ) {
            toast.error(
              'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
            );
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const closeExcelReview = () => {
    setExcelReviewOpen(false);
    setExcelResult(null);
  };

  const handleExcelFileSelect = (event) => {
    const fileTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const selectedFile = event.target.files[0];
    const importTab = activeTab;
    event.target.value = '';

    if (!selectedFile) return;
    if (!fileTypes.includes(selectedFile.type)) {
      toast.error('Lütfen Excel dosyası seçiniz.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      setExcelProcessing(true);
      setExcelStep({
        label: 'Excel dosyası okunuyor...',
        current: 0,
        total: 4,
      });

      try {
        const workbook = XLSX.read(ev.target.result, { type: 'buffer' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        // header: 1 -> her satırı [1.sütun, 2.sütun, 3.sütun] dizisi olarak
        // döndürür; böylece başlık metninin ne yazdığı önemli olmaz.
        const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          blankrows: false,
        });

        if (sheetRows.length <= 1) {
          toast.error('Excel dosyası boş.');
          setExcelProcessing(false);
          return;
        }

        const dataRows = sheetRows.slice(1);

        const seenAccountCariPairs = new Set();
        const parsedRows = dataRows
          .map(mapExcelRowByPosition)
          .filter((row) => row.accountCode || row.cariKod)
          .filter((row) => {
            const pairKey = `${row.accountCode}||${row.cariKod}`;
            if (seenAccountCariPairs.has(pairKey)) return false;
            seenAccountCariPairs.add(pairKey);
            return true;
          });

        if (!parsedRows.length) {
          toast.error(
            'Excel içeriği tanınamadı. 1. sütun Muhasebe Kodu, 2. sütun Cari Kod olmalı.'
          );
          setExcelProcessing(false);
          return;
        }

        setExcelStep({
          label: 'AVM listesi getiriliyor...',
          current: 1,
          total: 4,
        });
        const mallsResponse = await api.post('/malls/get-malls', {
          withOffices: true,
        });
        const malls = Array.isArray(mallsResponse?.data?.result)
          ? mallsResponse.data.result
          : [];

        const mallByAccountCode = new Map();
        malls.forEach((mall) => {
          const code = String(
            mall?.accountCode || mall?.account_code || ''
          ).trim();
          if (code && !mallByAccountCode.has(code)) {
            mallByAccountCode.set(code, mall);
          }
        });

        const rowsByMallID = new Map();
        const unmatchedMallCodes = new Set();

        parsedRows.forEach((row) => {
          if (!row.accountCode || !row.cariKod) return;
          const mall = mallByAccountCode.get(row.accountCode);
          if (!mall) {
            unmatchedMallCodes.add(row.accountCode);
            return;
          }
          const mallID = mall.id ?? mall.mallID;
          if (!rowsByMallID.has(mallID)) {
            rowsByMallID.set(mallID, { mall, rows: [] });
          }
          rowsByMallID.get(mallID).rows.push(row);
        });

        const mallEntries = Array.from(rowsByMallID.values());
        const groups = [];
        const unmatchedCariRows = [];

        for (let i = 0; i < mallEntries.length; i += 1) {
          const { mall, rows } = mallEntries[i];
          const mallTitle = mall.title || '-';

          setExcelStep({
            label: `${mallTitle} şubeleri getiriliyor... (${i + 1}/${
              mallEntries.length
            })`,
            current: 2,
            total: 4,
          });

          // eslint-disable-next-line no-await-in-loop
          const branchesResponse = await api.post('flow/get-malls-branches', {
            mallID: mall.id ?? mall.mallID,
          });
          const branches = Array.isArray(branchesResponse?.data?.result)
            ? branchesResponse.data.result
            : [];

          const branchesByCariKod = new Map();
          branches.forEach((branch) => {
            const code = String(branch?.companyCorporationalCode || '').trim();
            if (!code) return;
            if (!branchesByCariKod.has(code)) {
              branchesByCariKod.set(code, []);
            }
            branchesByCariKod.get(code).push(branch);
          });

          const matchedStores = [];
          rows.forEach((row) => {
            const matchingBranches = branchesByCariKod.get(row.cariKod);
            if (!matchingBranches || !matchingBranches.length) {
              unmatchedCariRows.push({
                mallTitle,
                accountCode: row.accountCode,
                cariKod: row.cariKod,
              });
              return;
            }
            matchingBranches?.forEach((branch) => {
              matchedStores.push({
                branchID: branch.branchID || branch.id,
                storeTitle: branch.title || '-',
                cariKod: row.cariKod,
                vergiNo: branch?.billing?.taxNo || '-',
                message: row.message,
                warnings: Array.isArray(branch.warnings) ? branch.warnings : [],
              });
            });
          });

          if (matchedStores.length) {
            groups.push({
              mallID: mall.id ?? mall.mallID,
              mallTitle,
              accountCode: String(
                mall.accountCode || mall.account_code || ''
              ).trim(),
              stores: matchedStores,
            });
          }
        }

        setExcelStep({
          label: 'Sonuçlar hazırlanıyor...',
          current: 3,
          total: 4,
        });

        if (!groups.length) {
          toast.error('Eşleşen mağaza bulunamadı.');
          setExcelProcessing(false);
          return;
        }

        setExcelResult({
          actionTab: importTab,
          groups,
          unmatchedMallCodes: Array.from(unmatchedMallCodes),
          unmatchedCariRows,
        });
        setExcelProcessing(false);
        setExcelReviewOpen(true);
      } catch (error) {
        toast.error(
          error?.message
            ? `Excel dosyası işlenirken bir hata oluştu: ${error.message}`
            : 'Excel dosyası işlenirken bir hata oluştu.'
        );
        setExcelProcessing(false);
      }
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleConfirmExcelImport = async () => {
    if (!excelResult || excelSubmitting) return;

    const totalStores = excelResult.groups.reduce(
      (acc, group) => acc + group.stores.length,
      0
    );
    if (!totalStores) {
      toast.error('Eklenecek mağaza bulunamadı.');
      return;
    }

    const user = tokenService.getUser();
    const token = user?.token;
    const userName = user?.name || user?.email || 'Yönetici';
    const isBlockImport = excelResult.actionTab === TAB_BLOCK;

    setExcelSubmitting(true);
    setExcelProcessing(true);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < excelResult.groups.length; i += 1) {
      const group = excelResult.groups[i];
      setExcelStep({
        label: isBlockImport
          ? `${group.mallTitle} için blokaj işlemi gönderiliyor... (${i + 1}/${
              excelResult.groups.length
            })`
          : `${group.mallTitle} için uyarılar gönderiliyor... (${i + 1}/${
              excelResult.groups.length
            })`,
        current: i,
        total: excelResult.groups.length,
      });

      if (isBlockImport) {
        try {
          const branchIDs = group.stores.map((store) => store.branchID);
          // eslint-disable-next-line no-await-in-loop
          const response = await Services.BlockBranch(token, branchIDs);
          if (response?.data?.status === false) {
            throw new Error(response?.data?.message || 'Blokaja alınamadı.');
          }

          const storesWithWarnings = group.stores.filter(
            (store) => store.warnings.length > 0
          );

          if (storesWithWarnings.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(
              storesWithWarnings.flatMap((store) =>
                store.warnings.map((warning) =>
                  Services.RemoveBranchWarning(token, warning.warningID)
                )
              )
            );

            storesWithWarnings.forEach((store) => {
              appendMallAlertBlockRecord({
                mallID: group.mallID,
                mallTitle: group.mallTitle,
                branchID: store.branchID,
                storeTitle: store.storeTitle,
                taxNo: store.vergiNo,
                userName,
                type: ACTION_TYPES.WARNING_REMOVED,
                message: '',
              });
            });
          }

          group.stores.forEach((store) => {
            appendMallAlertBlockRecord({
              mallID: group.mallID,
              mallTitle: group.mallTitle,
              branchID: store.branchID,
              storeTitle: store.storeTitle,
              taxNo: store.vergiNo,
              userName,
              type: ACTION_TYPES.BLOCKED,
              message: '',
            });
          });

          successCount += group.stores.length;
        } catch (error) {
          failCount += group.stores.length;
        }
        // eslint-disable-next-line no-continue
        continue;
      }

      const storesByMessage = new Map();
      group.stores.forEach((store) => {
        const key = store.message || '';
        if (!storesByMessage.has(key)) storesByMessage.set(key, []);
        storesByMessage.get(key).push(store);
      });

      // eslint-disable-next-line no-restricted-syntax
      for (const [
        warningMessage,
        storesForMessage,
      ] of storesByMessage.entries()) {
        try {
          const branchIDs = storesForMessage.map((store) => store.branchID);
          // eslint-disable-next-line no-await-in-loop
          const response = await Services.AddBranchWarning(
            token,
            branchIDs,
            warningMessage
          );
          if (response?.data?.status === false) {
            throw new Error(
              response?.data?.message || 'Uyarı listesine eklenemedi.'
            );
          }

          storesForMessage.forEach((store) => {
            appendMallAlertBlockRecord({
              mallID: group.mallID,
              mallTitle: group.mallTitle,
              branchID: store.branchID,
              storeTitle: store.storeTitle,
              taxNo: store.vergiNo,
              userName,
              type: ACTION_TYPES.WARNING_ADDED,
              message: warningMessage,
            });
          });

          successCount += storesForMessage.length;
        } catch (error) {
          failCount += storesForMessage.length;
        }
      }
    }

    setExcelStep({
      label: 'Tamamlandı',
      current: excelResult.groups.length,
      total: excelResult.groups.length,
    });
    setExcelProcessing(false);
    setExcelSubmitting(false);
    closeExcelReview();

    if (successCount) {
      toast.success(
        isBlockImport
          ? `${successCount} mağaza blokaja alındı.`
          : `${successCount} mağaza uyarı listesine eklendi.`
      );
    }
    if (failCount) {
      toast.error(`${failCount} mağaza için işlem başarısız oldu.`);
    }

    await loadStores();
  };

  const clearFilters = () => {
    setFilters({
      cariKod: '',
      ticariUnvan: '',
      vergiNo: '',
    });
    setSelectedMalls([]);
    setStatusFilter(STATUS_ALL);
    setAppliedFilter(null);
    setAppliedMallIDs([]);
    setStart(0);
    setPageOffsets({ 0: 0 });
    loadStores(0, limit, null);
    loadTotalRecord(null);
  };

  return (
    <>
      <Helmet>
        <title>Uyarı ve Blokaj Sistemi</title>
      </Helmet>

      <Stack spacing={3} sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Uyarı ve Blokaj Sistemi
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Tüm AVM&apos;lerdeki mağazaları listeleyip uyarı ya da blokaj işlemi
            başlatabilirsiniz.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ px: 2, pt: 1.5, borderBottom: '1px solid #E5E7EB' }}
          >
            <Tab label="Uyarı Sistemi" value={TAB_WARNING} />
            <Tab label="Blokaj Sistemi" value={TAB_BLOCK} />
          </Tabs>

          <Stack spacing={2} sx={{ p: 2.25 }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              justifyContent={{ lg: 'flex-end' }}
            >
              <TextField
                placeholder="Cari Kod"
                size="small"
                fullWidth
                value={filters.cariKod}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    cariKod: event.target.value,
                  }))
                }
                sx={{
                  maxWidth: { lg: 200 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                  },
                }}
              />
              <TextField
                placeholder="Cari Ünvan"
                size="small"
                fullWidth
                value={filters.ticariUnvan}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    ticariUnvan: event.target.value,
                  }))
                }
                sx={{
                  maxWidth: { lg: 220 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                  },
                }}
              />
              <TextField
                placeholder="Vergi Numarası"
                size="small"
                fullWidth
                value={filters.vergiNo}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    vergiNo: event.target.value,
                  }))
                }
                sx={{
                  maxWidth: { lg: 200 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: '100%', lg: 240 },
                  maxWidth: { lg: 280 },
                  flexShrink: 0,
                }}
              >
                <InputLabel>AVM</InputLabel>
                <Select
                  multiple
                  value={selectedMalls.map((mall) => mall.id)}
                  onChange={(event) => {
                    const { value } = event.target;
                    setSelectedMalls(
                      mallOptions.filter((mall) => value.includes(mall.id))
                    );
                  }}
                  input={
                    <OutlinedInput
                      label="AVM"
                      sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF' }}
                    />
                  }
                  onClose={() => setMallSearch('')}
                  MenuProps={{
                    disableAutoFocusItem: true,
                    PaperProps: { style: { maxHeight: 320 } },
                  }}
                  renderValue={(selected) => (
                    <Stack
                      direction="row"
                      flexWrap="nowrap"
                      alignItems="center"
                      gap={0.5}
                      sx={{ overflow: 'hidden' }}
                    >
                      {selected.slice(0, 1).map((id) => {
                        const mall = mallOptions.find((m) => m.id === id);
                        return (
                          <Chip
                            key={id}
                            label={mall?.title || id}
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 20,
                              maxWidth: 130,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        );
                      })}
                      {selected.length > 1 && (
                        <Chip
                          label={`+${selected.length - 1}`}
                          size="small"
                          sx={{ fontSize: 11, height: 20, flexShrink: 0 }}
                        />
                      )}
                    </Stack>
                  )}
                >
                  <ListSubheader
                    sx={{ p: 1, lineHeight: 'unset' }}
                    onClickCapture={(event) => event.stopPropagation()}
                  >
                    <TextField
                      inputRef={mallSearchRef}
                      size="small"
                      placeholder="AVM ara..."
                      fullWidth
                      value={mallSearch}
                      onChange={(event) => setMallSearch(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                    />
                  </ListSubheader>
                  {mallOptions
                    .filter((mall) =>
                      mall.title
                        .toLowerCase()
                        .includes(mallSearch.toLowerCase())
                    )
                    .map((mall) => {
                      const isSelected = selectedMalls.some(
                        (m) => m.id === mall.id
                      );
                      return (
                        <MenuItem key={mall.id} value={mall.id}>
                          <CheckIcon
                            sx={{
                              fontSize: 16,
                              mr: 1,
                              color: isSelected ? '#22c55e' : 'transparent',
                            }}
                          />
                          {mall.title}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: '100%', lg: 190 },
                  flexShrink: 0,
                }}
              >
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleChangeStatusFilter}
                  input={
                    <OutlinedInput
                      label="Durum"
                      sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF' }}
                    />
                  }
                >
                  {STATUS_FILTER_OPTIONS[activeTab].map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: { xs: '100%', lg: 'auto' } }}
              >
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  disabled={filtersAreEmpty || loading}
                  sx={{
                    minWidth: 74,
                    height: 42,
                    borderRadius: '10px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Filtrele
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={
                    loading ||
                    (filtersAreEmpty &&
                      !isFiltered &&
                      statusFilter === STATUS_ALL)
                  }
                  sx={{
                    minWidth: 78,
                    height: 42,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#CBD5E1',
                    color: '#475569',
                  }}
                >
                  Temizle
                </Button>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', lg: 'center' }}
            >
              {selectedStores.length > 0 ? (
                <Button
                  variant="contained"
                  onClick={() => setModalOpen(true)}
                  sx={{
                    minWidth: { xs: '100%', lg: 240 },
                    height: 34,
                    px: 2,
                    borderRadius: '9px',
                    alignSelf: { xs: 'stretch', lg: 'flex-start' },
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: 500,
                    backgroundColor: actionPalette.main,
                    '&:hover': {
                      backgroundColor: actionPalette.hover,
                    },
                  }}
                  startIcon={<Iconify icon={actionIcon} width={16} />}
                >
                  {`${selectedStores.length} Mağazayı ${verbLabel}`}
                </Button>
              ) : (
                <Box />
              )}
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignSelf: { xs: 'stretch', lg: 'center' } }}
              >
                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={filteredStores.length === 0}
                  sx={{
                    height: 32,
                    px: 2,
                    borderRadius: '9px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#CBD5E1',
                    color: '#475569',
                  }}
                  startIcon={
                    <Iconify icon="solar:download-outline" width={16} />
                  }
                >
                  Excel&apos;e Aktar
                </Button>
                <Button
                  component="label"
                  variant="outlined"
                  sx={{
                    height: 32,
                    px: 2,
                    borderRadius: '9px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#BFD3FF',
                    color: '#2563EB',
                  }}
                  startIcon={<Iconify icon="solar:upload-outline" width={16} />}
                >
                  Excel&apos;den Aktar
                  <Input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    sx={{ display: 'none' }}
                    onChange={handleExcelFileSelect}
                  />
                </Button>
                <TablePagination
                  component="div"
                  count={totalRecord}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={limit}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Sayfa başına"
                  labelDisplayedRows={displayedRowsLabel}
                  SelectProps={{ disabled: loading || totalLoading }}
                  nextIconButtonProps={{
                    disabled: loading || totalLoading || page >= lastPage,
                  }}
                  backIconButtonProps={{
                    disabled: loading || totalLoading || page === 0,
                  }}
                  sx={{
                    ...COMPACT_PAGINATION_SX,
                    '& .MuiTablePagination-toolbar': {
                      ...COMPACT_PAGINATION_SX['& .MuiTablePagination-toolbar'],
                      pl: 0,
                    },
                    '& .MuiTablePagination-spacer': {
                      display: 'none',
                    },
                  }}
                />
              </Stack>
            </Stack>

            <TableContainer
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell align="center" sx={{ py: 1.25, width: 56 }}>
                      #
                    </TableCell>
                    <TableCell padding="checkbox" sx={{ py: 1.25 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedIds.length > 0 && !allSelected}
                        onChange={handleToggleAll}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>Muhasebe Kodu</TableCell>
                    <TableCell sx={{ py: 1.25 }}>Cari Kod</TableCell>
                    <TableCell sx={{ py: 1.25 }}>Ticari Ünvan</TableCell>
                    <TableCell sx={{ py: 1.25 }}>AVM Adı</TableCell>
                    <TableCell sx={{ py: 1.25 }}>Mağaza Adı</TableCell>
                    <TableCell sx={{ py: 1.25 }}>Vergi No</TableCell>
                    <TableCell align="center" sx={{ py: 1.25 }}>
                      {activeTab === TAB_WARNING ? 'Uyarı Durumu' : 'Durum'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStores.map((store, index) => {
                    const isChecked = selectedIds.includes(store.branchID);
                    const rowStatus = getRowActiveStatus(store, activeTab);
                    const isRowDisabled =
                      selectedStatus !== null && rowStatus !== selectedStatus;

                    return (
                      <TableRow
                        key={store.branchID}
                        hover
                        selected={isChecked}
                        sx={{
                          '& td': {
                            borderColor: '#F3F4F6',
                            py: 1.75,
                          },
                          opacity: isRowDisabled ? 0.5 : 1,
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{ color: 'text.secondary' }}
                        >
                          {displayStart + index + 1}
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isChecked}
                            disabled={isRowDisabled}
                            onChange={() => handleToggleOne(store.branchID)}
                          />
                        </TableCell>
                        <TableCell>{getAccountCode(store)}</TableCell>
                        <TableCell>{store.cariKod}</TableCell>
                        <TableCell>{store.ticariUnvan}</TableCell>
                        <TableCell>{store.mallTitle || '-'}</TableCell>
                        <TableCell>{store.storeTitle}</TableCell>
                        <TableCell>{store.vergiNo}</TableCell>
                        <TableCell align="center">
                          {activeTab === TAB_WARNING ? (
                            store.warnings.length > 0 ? (
                              <Chip
                                label="Uyarıda"
                                size="small"
                                sx={{
                                  ...statusChipSx,
                                  backgroundColor: '#FEF3C7',
                                  color: '#B45309',
                                }}
                              />
                            ) : (
                              <Chip
                                label="--"
                                size="small"
                                sx={{
                                  ...statusChipSx,
                                  backgroundColor: '#F3F4F6',
                                  color: '#6B7280',
                                }}
                              />
                            )
                          ) : (
                            <Chip
                              label={store.isBlocked ? 'Blokajlı' : 'Açık'}
                              size="small"
                              sx={{
                                ...statusChipSx,
                                backgroundColor: store.isBlocked
                                  ? '#FEE2E2'
                                  : '#DCFCE7',
                                color: store.isBlocked ? '#B91C1C' : '#166534',
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!loading && filteredStores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        Bu AVM için eşleşen mağaza bulunamadı.
                      </TableCell>
                    </TableRow>
                  )}

                  {loading && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        Liste yükleniyor...
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecord}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Sayfa başına"
              labelDisplayedRows={displayedRowsLabel}
              SelectProps={{ disabled: loading || totalLoading }}
              nextIconButtonProps={{
                disabled: loading || totalLoading || page >= lastPage,
              }}
              backIconButtonProps={{
                disabled: loading || totalLoading || page === 0,
              }}
              sx={COMPACT_PAGINATION_SX}
            />
          </Stack>
        </Card>
      </Stack>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ px: 3.5, pt: 3.5, pb: 2 }}>
          <Stack
            spacing={2.25}
            alignItems="center"
            sx={{ position: 'relative' }}
          >
            <IconButton
              onClick={() => setModalOpen(false)}
              sx={{
                position: 'absolute',
                top: -6,
                right: -8,
                color: '#94A3B8',
              }}
            >
              <Iconify icon="eva:close-fill" width={20} />
            </IconButton>

            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: actionPalette.soft,
                color: actionPalette.softFg,
              }}
            >
              <Iconify icon={actionIcon} width={28} />
            </Box>

            <Stack spacing={1} alignItems="center">
              <Typography
                variant="h5"
                sx={{ fontSize: 22, fontWeight: 700, textAlign: 'center' }}
              >
                {activeTab === TAB_WARNING && !isRemoveFlow
                  ? 'Uyarı Listesine Ekle'
                  : `${verbLabel} İstediğinize Emin misiniz?`}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#667085', textAlign: 'center' }}
              >
                {activeTab === TAB_WARNING
                  ? isRemoveFlow
                    ? `Seçilen ${selectedStores.length} mağazanın uyarısı kaldırılacaktır.`
                    : `Seçilen ${selectedStores.length} mağazaya sarı uyarı başlığı gösterilecektir.`
                  : isRemoveFlow
                  ? `Seçilen ${selectedStores.length} mağazanın blokajı kaldırılacaktır.`
                  : `Seçilen ${selectedStores.length} mağaza blokajlı statüsüne alınacaktır.`}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent="center"
            >
              {selectedStores.map((store) => (
                <Chip
                  key={store.branchID}
                  label={`${store.storeTitle} - ${store.mallTitle}`}
                  sx={{
                    backgroundColor: actionPalette.soft,
                    color: actionPalette.softFg,
                  }}
                />
              ))}
            </Stack>

            {activeTab === TAB_WARNING && !isRemoveFlow && (
              <TextField
                multiline
                minRows={4}
                placeholder="Uyarı mesajı (isteğe bağlı)..."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                fullWidth
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ px: 3.5, pb: 3.5, pt: 0, justifyContent: 'center' }}
        >
          <Button
            onClick={() => setModalOpen(false)}
            color="inherit"
            variant="outlined"
            disabled={submitting}
            sx={{
              minWidth: 88,
              height: 34,
              borderRadius: '10px',
              textTransform: 'none',
              borderColor: '#CBD5E1',
              color: '#475569',
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAction}
            disabled={submitting}
            sx={{
              minWidth: 172,
              height: 36,
              borderRadius: '10px',
              textTransform: 'none',
              boxShadow: 'none',
              backgroundColor: actionPalette.main,
              '&:hover': {
                backgroundColor: actionPalette.hover,
              },
            }}
            startIcon={<Iconify icon={actionIcon} />}
          >
            {verbLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={excelProcessing}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center' }}
            >
              {excelStep.label}
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant={excelStep.total ? 'determinate' : 'indeterminate'}
                value={
                  excelStep.total
                    ? (excelStep.current / excelStep.total) * 100
                    : undefined
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Lütfen bekleyin...
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={excelReviewOpen}
        onClose={closeExcelReview}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 3.5 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Excel İçe Aktarım Önizleme</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {excelIsBlockImport
                ? 'Onayladığınızda aşağıdaki mağazalar blokaja alınacaktır.'
                : 'Onayladığınızda aşağıdaki mağazalar uyarı listesine eklenecektir.'}
            </Typography>

            <Stack
              spacing={2}
              sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}
            >
              {excelResult?.groups.map((group) => (
                <Box
                  key={group.mallID}
                  sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 1.75 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {`${group.mallTitle} AVM'sinde ${excelGroupLabel} (${group.stores.length})`}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {group.stores.map((store) => (
                      <Chip
                        key={store.branchID}
                        label={`${store.storeTitle} (${store.cariKod})`}
                        size="small"
                        sx={{
                          backgroundColor: excelPalette.soft,
                          color: excelPalette.softFg,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}

              {excelResult &&
                (excelResult.unmatchedMallCodes.length > 0 ||
                  excelResult.unmatchedCariRows.length > 0) && (
                  <Box
                    sx={{
                      border: '1px solid #FCA5A5',
                      borderRadius: 2,
                      p: 1.75,
                      backgroundColor: '#FEF2F2',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: '#B91C1C', mb: 1 }}
                    >
                      Eşleşmeyen Kayıtlar
                    </Typography>
                    <Stack spacing={0.5}>
                      {excelResult.unmatchedMallCodes.map((code) => (
                        <Typography
                          key={code}
                          variant="body2"
                          sx={{ color: '#B91C1C' }}
                        >
                          {`Muhasebe kodu bulunamadı: ${code}`}
                        </Typography>
                      ))}
                      {excelResult.unmatchedCariRows.map((row, idx) => (
                        <Typography
                          key={`${row.accountCode}-${row.cariKod}-${idx}`}
                          variant="body2"
                          sx={{ color: '#B91C1C' }}
                        >
                          {`${row.mallTitle}: ${row.cariKod} cari koduna sahip mağaza bulunamadı`}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ px: 3.5, pb: 3.5, pt: 0, justifyContent: 'center' }}
        >
          <Button
            onClick={closeExcelReview}
            color="inherit"
            variant="outlined"
            disabled={excelSubmitting}
            sx={{
              minWidth: 120,
              height: 36,
              borderRadius: '10px',
              textTransform: 'none',
              borderColor: '#CBD5E1',
              color: '#475569',
            }}
          >
            Kapat
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmExcelImport}
            disabled={excelSubmitting}
            sx={{
              minWidth: 160,
              height: 36,
              borderRadius: '10px',
              textTransform: 'none',
              boxShadow: 'none',
              backgroundColor: excelPalette.main,
              '&:hover': { backgroundColor: excelPalette.hover },
            }}
            startIcon={<Iconify icon={excelActionIcon} width={16} />}
          >
            Onaylıyorum
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
